import { Fragment, useCallback, useEffect, useMemo } from "react";
import { type MenuProps as MUIMenuProps, navigationMenuPaperStyles } from "~/common/components/Menu/Menu";
import { MenuSpacer } from "~/common/components/Menu/MenuItem";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { NOTIFICATIONS_PAGE, NOTIFICATIONS_SETTINGS_PAGE } from "~/utils/url/staticUrls";
import { IMG_EMPTY_NOTIFICATIONS } from "~/utils/url/staticResources";
import { NotificationsItem } from "~/features/navigation/components/Notifications/NotificationItem";
import { type NotificationDTO } from "~/features/notifications/api/dto";
import { markNotificationsAsReadThunk, openNotificationsMenuThunk } from "~/features/notifications/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsNotificationsLoading,
  selectNotifications,
} from "~/features/notifications/redux/NotificationsSelectors";
import Spinner from "~/common/components/Spinner/Spinner";
import { useTranslation } from "react-i18next";
import { Link } from "~/common/components/Link";
import { Box, Menu, menuClasses, styled, Typography } from "~/common/mui";

interface MenuProps extends MUIMenuProps {
  onClose: () => void;
  children?: never;
}

const MENU_NOTIFICATION_AMOUNT = 3;

const StyledMenu = styled(Menu)({
  [`& .${menuClasses.paper}`]: {
    ...navigationMenuPaperStyles,

    width: "328px",
  },
});

export const NotificationsMenu = ({ onClose, open, ...props }: MenuProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(openNotificationsMenuThunk());
    }
  }, [dispatch, open]);

  const isLoading = useSelector(selectIsNotificationsLoading);
  const allNotifications = useSelector(selectNotifications);
  const results = useMemo(() => allNotifications.slice(0, MENU_NOTIFICATION_AMOUNT), [allNotifications]);

  const readNotification = useCallback(
    async (item: NotificationDTO) => dispatch(markNotificationsAsReadThunk(item.uid, !item.read)),
    [dispatch],
  );

  return (
    <StyledMenu
      onClose={onClose}
      open={open}
      {...MENU_POS_RIGHT}
      {...props}
      slotProps={{
        list: {
          disablePadding: true,
          // @ts-expect-error - data attribute...
          "data-ta-spec": "notification-main-box",
        },
      }}
    >
      <MenuHeader>
        <Typography variant="titleMedium">{t("header.link.my.notification")}</Typography>
        <Link href={NOTIFICATIONS_PAGE} data-ta-spec={"notification-view-all"}>
          {t("notifications.viewAll")}
        </Link>
      </MenuHeader>

      <MenuSpacer fullwidth={true} />
      {isLoading && (
        <Box sx={{ p: 2 }}>
          <Spinner />
        </Box>
      )}
      {!isLoading &&
        results.map((item, i) => (
          <Fragment key={i}>
            <NotificationsItem notification={item} readMessage={readNotification} />
            {i < results.length - 1 && <MenuSpacer fullwidth={true} />}
          </Fragment>
        ))}
      {!isLoading && !results.length && <NotificationNoResults />}
    </StyledMenu>
  );
};

const MenuHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2),
}));

const NotificationNoResults = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", pb: 2 }}>
      <img src={IMG_EMPTY_NOTIFICATIONS} alt="no notifications" />
      <Typography variant="headlineSmall">{t("notification.noResults.title")}</Typography>
      <Typography variant="labelLarge" sx={{ mb: 2 }}>
        {t("notification.noResults.description")}
      </Typography>
      <Typography
        component={Link}
        variant="labelLarge"
        href={NOTIFICATIONS_SETTINGS_PAGE}
        data-ta-spec={"notification-settings"}
      >
        {t("notification.settings")}
      </Typography>
    </Box>
  );
};
